import { Flags } from '../../enums/launch-darkly';
import { CompanyModuleId } from '../../services/react-query/query/user/types';

// Tela Bio gets its own sidebar in the webapp (see useUserMenuItems).
export const TELA_BIO_COMPANY_ID = 3401;

export interface NavigationVisibility {
  catalog: boolean;
  orders: boolean;
  quotes: boolean;
  retention: boolean;
  whitespace: boolean;
  resources: boolean;
  presentations: boolean;
  snippets: boolean;
}

interface GetNavigationVisibilityParams {
  flags: Record<string, unknown>;
  companyModuleIds: number[];
  companyId?: number;
}

/**
 * Which features the webapp sidebar offers the user. The Outlook add-in home
 * grid uses this so both surfaces show the same options — any change to the
 * sidebar conditions (webapp Layouts/Main/Sidebar/MenuItems/items/user) must
 * be mirrored here.
 */
export const getNavigationVisibility = ({
  flags,
  companyModuleIds,
  companyId
}: GetNavigationVisibilityParams): NavigationVisibility => {
  const flag = (key: Flags) => !!flags?.[key];
  const hasModule = (id: CompanyModuleId) => companyModuleIds.includes(id);

  const resources =
    hasModule(CompanyModuleId.Resources) && flag(Flags.RESOURCES);
  // Presentations and Snippets live under the Workspaces group, which has its
  // own flag on top of theirs.
  const workspaces = flag(Flags.WORKSPACES);
  const presentations =
    workspaces &&
    hasModule(CompanyModuleId.Presentations) &&
    flag(Flags.MY_PRESENTATIONS);
  const snippets = workspaces && flag(Flags.SNIPPETS);
  const catalog = hasModule(CompanyModuleId.Products) && flag(Flags.PRODUCTS);

  if (Number(companyId) === TELA_BIO_COMPANY_ID)
    return {
      resources,
      presentations,
      snippets,
      // Catalog sits under Revenue Management, which has its own flag.
      catalog: catalog && flag(Flags.DIGITAL_SHOWROOM_TELA_BIO_ONE),
      orders: false,
      quotes: false,
      retention: false,
      whitespace: false
    };

  return {
    resources,
    presentations,
    snippets,
    catalog,
    // V2 gets a top-level entry, V1 an entry under Sales — Orders either way.
    orders: flag(Flags.ORDERS) || flag(Flags.ORDERS_V2),
    quotes: flag(Flags.QUOTES) || flag(Flags.QUOTES_V2),
    retention: flag(Flags.ORDERS_REORDER),
    whitespace: flag(Flags.WHITESPACE_INTELLIGENCE)
  };
};
